import { Component, Inject, ChangeDetectorRef, ChangeDetectionStrategy, AfterViewInit, ViewChild, ElementRef } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { AuthService } from '../../../../services/auth.service';
import { JobTitle } from '../../../../interfaces/job-title.interface';
import { PhoneFormatPipe } from '../../../../shared/pipes';
import { User, UserDialogData } from '../../../../interfaces/auth.interface';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../../../shared/components/confirm-dialog/confirm-dialog';
import {
  validateEmailValue,
  blockAccentedCharsOnKeyDown,
  stripNonAsciiChars,
  validatePhoneNumber,
  blockExcessPhoneDigitsOnKeyDown,
  cleanPhoneValue,
  formatPhoneNumber,
  calculateCursorPosition
} from '../../../../shared/validators';

@Component({
  selector: 'app-add-colleague-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatCheckboxModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatSnackBarModule
],
  templateUrl: './add-colleague-dialog.html',
  styleUrls: ['./add-colleague-dialog.scss']
})
export class AddColleagueDialog implements AfterViewInit {
  @ViewChild('phoneField') phoneField!: ElementRef<HTMLInputElement>;

  colleagueData: Partial<User> = {
    name: '',
    username: '',
    jobs: [],
    email: '',
    phone: '',
    is_active: true
  };
  
  selectedJobs: string[] = [];
  errorMessage: string = '';
  isLoading: boolean = false;
  isResettingPassword: boolean = false;
  isPhoneNumberValid: boolean | null = null;
  emailError: string = '';
  private _isFormValidCache: boolean = true; // Initialize as true to avoid initial change
  private phoneFormatPipe = new PhoneFormatPipe();

  constructor(
    public dialogRef: MatDialogRef<AddColleagueDialog>,
    @Inject(MAT_DIALOG_DATA) public data: UserDialogData,
    private authService: AuthService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {
    // Ha szerkesztés módban vagyunk, töltse be a meglévő adatokat
    if (data.editing && data.colleague) {
      this.colleagueData = { ...data.colleague };
      this.selectedJobs = [...(data.colleague.jobNames || [])];
    } else {
      // Új kolléga esetén +36 előtag a telefonszám mezőbe
      this.colleagueData.phone = '+36 ';
    }
  }

  ngAfterViewInit(): void {
    // Initialize form validity cache asynchronously to avoid ExpressionChangedAfterItHasBeenCheckedError
    Promise.resolve().then(() => {
      this.updateFormValidCache();
      if (this.data.editing && this.data.colleague) {
        this.validatePhoneNumber();
      }
      this.cdr.markForCheck();
    });
  }

  onFieldInput(): void {
    // Defer validation to avoid ExpressionChangedAfterItHasBeenCheckedError
    Promise.resolve().then(() => {
      this.updateFormValidCache();
      this.cdr.markForCheck();
    });
  }

  onEmailInput(): void {
    Promise.resolve().then(() => {
      this.validateEmail();
      this.updateFormValidCache();
      this.cdr.markForCheck();
    });
  }

  onEmailPaste(): void {
    setTimeout(() => {
      const currentValue = this.colleagueData.email || '';
      const filtered = stripNonAsciiChars(currentValue);
      if (filtered !== currentValue) {
        this.colleagueData.email = filtered;
        this.emailError = 'Ékezetes karakterek nem megengedettek az email címben';
      } else {
        this.validateEmail();
      }
      this.updateFormValidCache();
      this.cdr.markForCheck();
    }, 0);
  }

  blockAccentedCharsInEmail(event: KeyboardEvent): void {
    blockAccentedCharsOnKeyDown(event);
  }

  private validateEmail(): void {
    this.emailError = validateEmailValue(this.colleagueData.email || '');
  }

  onPhoneKeyDown(event: KeyboardEvent): void {
    blockExcessPhoneDigitsOnKeyDown(event, this.colleagueData.phone || '');
  }

  onPhoneNumberInput(): void {
    Promise.resolve().then(() => {
      const input = this.phoneField?.nativeElement;
      const prevValue = this.colleagueData.phone || '';
      const prevCursorPos = input?.selectionStart ?? prevValue.length;

      const cleaned = cleanPhoneValue(prevValue);
      const formatted = formatPhoneNumber(cleaned);
      const newCursorPos = calculateCursorPosition(formatted, prevValue, prevCursorPos);

      this.colleagueData.phone = formatted;
      if (input) {
        setTimeout(() => {
          input.value = formatted;
          input.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
      }

      this.isPhoneNumberValid = validatePhoneNumber(formatted);
      this.updateFormValidCache();
      this.cdr.markForCheck();
    });
  }

  private validatePhoneNumber(value?: string): void {
    const phoneNumber = value ?? (this.colleagueData.phone || '');
    this.isPhoneNumberValid = validatePhoneNumber(phoneNumber);
    this.updateFormValidCache();
  }

  updateFormValidCache(): void {
    // Clear error message when user modifies the form
    if (this.errorMessage) {
      this.errorMessage = '';
    }
    
    const basicValid = (this.colleagueData.name || '').trim().length > 0 &&
                      (this.colleagueData.username || '').trim().length > 0 &&
                      (this.colleagueData.email || '').trim().length > 0 &&
                      this.selectedJobs.length > 0;

    // Ha van telefonszám, akkor validnak kell lennie
    // null = üres/semleges (valid), true = valid, false = invalid
    const phoneValid = this.isPhoneNumberValid !== false;

    // Email validáció
    const emailValid = !this.emailError;
    
    this._isFormValidCache = basicValid && phoneValid && emailValid;
  }

  isJobSelected(jobTitle: string): boolean {
    return this.selectedJobs.includes(jobTitle);
  }

  onJobTitleToggle(jobTitle: string, checked: boolean): void {
    if (checked) {
      if (!this.selectedJobs.includes(jobTitle)) {
        this.selectedJobs.push(jobTitle);
      }
    } else {
      this.selectedJobs = this.selectedJobs.filter(j => j !== jobTitle);
    }
    this.updateFormValidCache();
  }

  isFormValid(): boolean {
    return this._isFormValidCache;
  }

  get formValid(): boolean {
    return this._isFormValidCache;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onResetPassword(): void {
    if (!this.data.colleague?.id) return;

    const confirmRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Jelszó visszaállítás',
        message: `Biztosan visszaállítod "${this.data.colleague.username || this.data.colleague.name}" jelszavát a felhasználónevére?`,
        type: 'modify'
      } as ConfirmDialogData
    });

    confirmRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.isResettingPassword = true;
      this.errorMessage = '';
      this.cdr.markForCheck();

      this.authService.resetPassword(this.data.colleague!.id!).subscribe({
        next: (response) => {
          this.isResettingPassword = false;
          this.snackBar.open(response.message || 'Jelszó sikeresen visszaállítva!', 'Bezár', {
            duration: 3000,
            panelClass: ['success-snackbar']
          });
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.isResettingPassword = false;
          this.errorMessage = error?.error?.detail || 'Hiba történt a jelszó visszaállítása során';
          this.cdr.detectChanges();
        }
      });
    });
  }

  onSave(): void {
    this.errorMessage = '';
    
    if (!this.isFormValid()) {
      this.errorMessage = 'Kérlek töltsd ki helyesen az összes kötelező mezőt!';
      return;
    }

    // Email validáció a shared validátor segítségével
    const emailError = validateEmailValue(this.colleagueData.email || '');
    if (emailError) {
      this.errorMessage = emailError;
      return;
    }

    this.isLoading = true;
    
    // Format phone number before sending to backend
    // Ha csak a +36 prefix van (bármennyi space-szel), akkor ne mentsük (üres stringként kezelendő)
    const rawPhone = (this.colleagueData.phone || '').trim();
    // Ellenőrizzük, hogy csak a +36 prefix van-e (opcionális space-ekkel)
    const isOnlyPrefix = /^\+36\s*$/.test(rawPhone);
    const formattedPhoneNumber = isOnlyPrefix 
      ? '' 
      : this.phoneFormatPipe.transform(rawPhone);
    
    // Get job title IDs from the selected values
    const jobTitleIds = this.selectedJobs
      .map(jobName => this.data.jobTitleObjects?.find((jt: any) => jt.title_name_hu === jobName))
      .filter((jt: any) => jt != null)
      .map((jt: any) => jt.id);
    
    if (jobTitleIds.length === 0) {
      this.errorMessage = 'Legalább egy munkakör kiválasztása kötelező!';
      return;
    }
    
    if (this.data.editing && this.data.colleague && this.data.colleague.id) {
      // Update existing colleague
      const userData = {
        username: this.colleagueData.username,
        fullname: this.colleagueData.name,
        email: this.colleagueData.email,
        phone_number: formattedPhoneNumber,
        job_title_ids: jobTitleIds,
        is_active: this.colleagueData.is_active
      };

      this.authService.updateUser(this.data.colleague.id, userData).subscribe({
        next: (response) => {
          this.isLoading = false;
          if (response.success) {
            this.snackBar.open(response.message || 'Kolléga sikeresen frissítve!', 'Bezár', { 
              duration: 3000,
              panelClass: ['success-snackbar']
            });
            this.dialogRef.close({
              success: true,
              message: response.message,
              shouldReload: true
            });
          } else {
            this.errorMessage = response.message || 'Hiba történt a felhasználó frissítése során';
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.handleBackendError(error);
          // Force change detection to ensure UI updates properly
          this.cdr.detectChanges();
        }
      });
    } else {
      // Create new colleague
      const userData = {
        username: this.colleagueData.username,
        fullname: this.colleagueData.name,
        email: this.colleagueData.email,
        phone_number: formattedPhoneNumber,
        password: this.colleagueData.username, // Use username as temporary password
        job_title_ids: jobTitleIds,
        is_active: this.colleagueData.is_active
      };

      this.authService.createUser(userData).subscribe({
        next: (response) => {
          this.isLoading = false;
          if (response.success) {
            this.snackBar.open(response.message || 'Kolléga sikeresen hozzáadva!', 'Bezár', { 
              duration: 3000,
              panelClass: ['success-snackbar']
            });
            this.dialogRef.close({
              success: true,
              message: response.message,
              shouldReload: true
            });
          } else {
            this.errorMessage = response.message || 'Hiba történt a felhasználó létrehozása során';
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.handleBackendError(error);
          // Force change detection to ensure UI updates properly
          this.cdr.detectChanges();
        }
      });
    }
  }

  private handleBackendError(error: any): void {
    // Handle different types of errors and show them in the dialog
    let errorMessage = 'Hiba történt a művelet során';
    let showSnackbar = true; // Control whether to show snackbar
    
    if (error.status === 400) {
      // Bad request - usually validation errors
      if (error.error?.detail) {
        errorMessage = error.error.detail;
      } else if (error.error?.message) {
        errorMessage = error.error.message;
      } else if (typeof error.error === 'string') {
        errorMessage = error.error;
      } else {
        errorMessage = 'Érvénytelen adatok. Kérjük, ellenőrizze a megadott információkat.';
      }
    } else if (error.status === 409) {
      // Conflict - usually duplicate data (username already exists)
      errorMessage = 'Ez a felhasználónév már használatban van.';
      showSnackbar = false; // Don't show snackbar for duplicate data errors
    } else if (error.status === 403) {
      // Forbidden - insufficient permissions
      errorMessage = 'Nincs jogosultsága ehhez a művelethez.';
    } else if (error.status === 422) {
      // Unprocessable Entity - validation errors
      if (error.error?.detail && Array.isArray(error.error.detail)) {
        // Pydantic validation errors
        const validationErrors = error.error.detail.map((err: any) => {
          if (err.loc && err.msg) {
            const field = err.loc[err.loc.length - 1];
            return `${field}: ${err.msg}`;
          }
          return err.msg || 'Validációs hiba';
        }).join(', ');
        errorMessage = `Validációs hiba: ${validationErrors}`;
      } else if (error.error?.detail) {
        errorMessage = error.error.detail;
      }
    } else if (error.status === 500) {
      errorMessage = 'Szerver hiba történt. Kérjük, próbálja meg később.';
    } else if (error.status === 0) {
      errorMessage = 'Kapcsolódási hiba. Ellenőrizze az internetkapcsolatot.';
    }
    
    // Always show error in dialog
    this.errorMessage = errorMessage;
    
    // Only show snackbar for certain error types
    if (showSnackbar) {
      this.snackBar.open(errorMessage, 'Bezár', { 
        duration: 5000,
        panelClass: ['error-snackbar']
      });
    }
  }
}
